import { useEffect, useState, useRef } from "react";
import Breadcrumbs from "../components/Breadcrumbs";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import ProductModal from "../components/ProductModal";
import { useCart } from "../context/CartContext";
import axios from "axios";
import useSearchedProducts from "../hooks/useSearchedProducts";

function Flowers() {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [flowerProducts, setFlowerProducts] = useState([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;

  const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";
  const { selectedFlowers, addFlower, removeFlower } = useCart();

  // ✅ Ref for the product grid
  const gridRef = useRef(null);

  // Fetch all flower products
  useEffect(() => {
    const fetchFlowers = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/products?category=flower`);
        setFlowerProducts(res.data);
      } catch (err) {
        console.error("Failed to fetch flower products:", err);
      }
    };
    fetchFlowers();
  }, [BASE_URL]);

  // Use custom hook to filter products based on search
  const { filteredProducts, isFiltered, resetProducts } = useSearchedProducts(flowerProducts);

  // Pagination calculations
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const currentProducts = filteredProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Add/remove flower
  const handleProductClick = (product) => {
    const exists = selectedFlowers.find((item) => item._id === product._id);
    exists ? removeFlower(product._id) : addFlower(product);
  };

  // View product in modal
  const handleViewProduct = (product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  // Reset search and pagination
  const handleResetProducts = () => {
    resetProducts();
    setCurrentPage(1);
    if (gridRef.current) {
      gridRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  // ✅ Scroll to top of grid on page change
  const handlePageChange = (pageNum) => {
    setCurrentPage(pageNum);
    if (gridRef.current) {
      gridRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div>
      {/* Breadcrumbs */}
      <div className="max-w-6xl mx-auto px-4">
        <Breadcrumbs />
      </div>

      {/* Hero Section */}
      <section className="bg-pink-50 py-20 text-center">
        <div className="max-w-6xl mx-auto px-4">
          <h1 className="text-4xl font-bold mb-4 text-pink-700">
            Our Flower Collection
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Browse our full selection of fresh flowers, curated for every special moment.
          </p>
        </div>
      </section>

      {/* Products Section */}
      <section className="px-4 sm:px-6 py-16 max-w-6xl mx-auto" data-aos="fade-up">
        {/* Show All Button when filtered via search */}
        {isFiltered && (
          <div className="flex justify-center mb-12">
            <button
              onClick={handleResetProducts}
              className="px-4 py-2 text-sm font-medium text-blue-600 border border-blue-400 rounded-full hover:bg-blue-600 hover:text-white transition"
            >
              Show All Flowers
            </button>
          </div>
        )}

        {/* Product Grid */}
        <div ref={gridRef} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 min-h-[400px]">
          {currentProducts.map((product, i) => (
            <div key={product._id} className="w-full">
              <ProductCard
                product={product}
                index={i}
                onClick={() => handleProductClick(product)}
                onView={() => handleViewProduct(product)}
                selected={selectedFlowers.some((f) => f._id === product._id)}
              />
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-wrap justify-center mt-6 gap-2">
            <button
              className="px-3 py-1 border rounded disabled:opacity-50"
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              Prev
            </button>

            {[...Array(totalPages)].map((_, i) => {
              const pageNum = i + 1;
              return (
                <button
                  key={pageNum}
                  className={`px-3 py-1 border rounded ${
                    pageNum === currentPage ? "bg-pink-600 text-white" : ""
                  }`}
                  onClick={() => handlePageChange(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              className="px-3 py-1 border rounded disabled:opacity-50"
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              Next
            </button>
          </div>
        )}
      </section>

      {/* Product Modal */}
      <ProductModal
        product={selectedProduct}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAdd={handleProductClick}
        selectedItems={selectedFlowers}
      />

      {/* Footer */}
      <div data-aos="fade-up">
        <Footer />
      </div>
    </div>
  );
}

export default Flowers;
